import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine,
} from "recharts";
import {
  Brain, Activity, AlertTriangle, OctagonAlert, UserSearch, ArrowRight, Sparkles, TrendingUp,
} from "lucide-react";

const PANIC_LEVEL_COLOR = {
  Critical: "hsl(var(--destructive))",
  High:     "hsl(var(--warning))",
  Elevated: "hsl(var(--primary))",
  Nominal:  "hsl(var(--success))",
};

const POI_STATUS = {
  Alert:       { color: "hsl(var(--destructive))", icon: OctagonAlert },
  Investigate: { color: "hsl(var(--warning))",     icon: AlertTriangle },
  Watch:       { color: "hsl(var(--primary))",     icon: UserSearch },
};

const heatColor = (v) => {
  if (v >= 0.8) return "hsl(var(--destructive))";
  if (v >= 0.6) return "hsl(var(--warning))";
  if (v >= 0.4) return "hsl(var(--primary))";
  if (v >= 0.2) return "hsl(217 91% 60%)";
  return "hsl(var(--muted-foreground))";
};

const congestionColor = (v) => {
  if (v >= 0.9)  return "hsl(var(--destructive))";
  if (v >= 0.75) return "hsl(var(--warning))";
  if (v >= 0.55) return "hsl(var(--primary))";
  return "hsl(217 91% 60%)";
};

const titleCase = (s) =>
  String(s || "").split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const relTime = (iso) => {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.max(1, Math.floor(diff))}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
};

const Pill = ({ icon: Icon, label, value, tone = "default", sub, testid }) => {
  const map = {
    default: "text-foreground",
    success: "sev-low",
    danger:  "sev-critical",
    warn:    "sev-medium",
    primary: "text-[hsl(var(--primary))]",
  };
  return (
    <div className="panel-elevated p-2.5 flex items-center gap-2" data-testid={testid}>
      <Icon className={`w-4 h-4 shrink-0 ${map[tone]}`} />
      <div className="min-w-0">
        <div className="micro-label leading-none">{label}</div>
        <div className={`font-display font-black text-lg leading-tight ${map[tone]}`} data-testid={`${testid}-value`}>
          {value}
        </div>
        {sub && <div className="micro-label text-[9px] mt-0.5 text-muted-foreground">{sub}</div>}
      </div>
    </div>
  );
};

const BehavioralAnomalyMatrix = ({ rows = [], types = [] }) => {
  if (rows.length === 0) {
    return <div className="text-[11px] text-muted-foreground py-4 text-center">No behavioral anomalies detected.</div>;
  }
  return (
    <div className="overflow-x-auto" data-testid="ai-anomaly-matrix">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-left font-mono text-[10px] text-muted-foreground border-b border-border">
            <th className="py-1.5 px-2 font-medium">Zone</th>
            {types.map((t) => (
              <th key={t.key} className="py-1.5 px-2 font-medium text-center" title={t.label}>
                {t.label}
              </th>
            ))}
            <th className="py-1.5 px-2 font-medium text-right">Peak</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.zone_id}
              className="border-b border-border last:border-b-0 hover:bg-[hsl(var(--surface-elevated))]"
              data-testid={`ai-anomaly-row-${r.zone_id}`}
            >
              <td className="py-1.5 px-2">
                <div className="truncate">{r.zone_name}</div>
                <div className="micro-label text-[8.5px] text-muted-foreground capitalize">{r.type}</div>
              </td>
              {r.anomalies.map((a) => {
                const pct = Math.round(a.score * 100);
                const color = heatColor(a.score);
                return (
                  <td key={a.key} className="py-1 px-1 text-center" title={`Last seen ${relTime(a.last_seen)}`}>
                    <div
                      className="inline-block min-w-[42px] py-1 px-2 font-mono text-[10px] font-bold"
                      style={{
                        background: color,
                        color: "hsl(var(--background))",
                        opacity: 0.3 + a.score * 0.7,
                      }}
                    >
                      {pct}
                    </div>
                  </td>
                );
              })}
              <td className="py-1.5 px-2 text-right font-mono font-bold" style={{ color: heatColor(r.peak_score) }}>
                {Math.round(r.peak_score * 100)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2 flex items-center gap-2 text-[10px] font-mono text-muted-foreground">
        <span>Scale:</span>
        {[0.2, 0.4, 0.6, 0.8, 1.0].map((v) => (
          <span key={v} className="inline-flex items-center gap-1">
            <span className="w-2.5 h-2.5 inline-block" style={{ background: heatColor(v) }} />
            {Math.round(v * 100)}
          </span>
        ))}
      </div>
    </div>
  );
};

const PanicForecastChart = ({ curve = [], level }) => {
  const series = curve.map((p) => ({
    horizon: p.horizon_min === 0 ? "Now" : `+${p.horizon_min}m`,
    panic: Math.round(p.panic * 100),
  }));
  const color = PANIC_LEVEL_COLOR[level] || "hsl(var(--primary))";
  return (
    <div className="h-44" data-testid="ai-panic-forecast">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={series} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
          <defs>
            <linearGradient id="panicFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.35} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="horizon" stroke="hsl(var(--muted-foreground))" fontSize={9} tickLine={false} />
          <YAxis stroke="hsl(var(--muted-foreground))" fontSize={9} tickLine={false} unit="%" width={32} domain={[0, 100]} />
          <Tooltip
            contentStyle={{ background: "hsl(var(--surface-elevated))", border: "1px solid hsl(var(--border))", fontSize: 11, padding: 6, borderRadius: 2 }}
            formatter={(v) => [`${v}%`, "Panic"]}
          />
          <ReferenceLine y={55} stroke="hsl(var(--warning))" strokeDasharray="3 3" strokeOpacity={0.6} />
          <ReferenceLine y={75} stroke="hsl(var(--destructive))" strokeDasharray="3 3" strokeOpacity={0.7} />
          <Area type="monotone" dataKey="panic" stroke={color} fill="url(#panicFill)" strokeWidth={1.75} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
      <div className="flex items-center justify-center gap-3 -mt-1 text-[10px] font-mono text-muted-foreground">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "hsl(var(--warning))" }} /> 55 Watch</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "hsl(var(--destructive))" }} /> 75 Alarm</span>
        <span>Level: <span className="font-bold" style={{ color }}>{level}</span></span>
      </div>
    </div>
  );
};

const SuspiciousActivityTable = ({ rows = [], onOpenIncident }) => {
  if (rows.length === 0) {
    return (
      <div className="text-[11px] text-muted-foreground py-4 text-center" data-testid="ai-pois-empty">
        No suspicious activity detected in window.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto" data-testid="ai-pois-table">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-left font-mono text-[10px] text-muted-foreground border-b border-border">
            <th className="py-1.5 px-2 font-medium">POI</th>
            <th className="py-1.5 px-2 font-medium">Zone · Camera</th>
            <th className="py-1.5 px-2 font-medium">Factors</th>
            <th className="py-1.5 px-2 font-medium text-right">Dwell</th>
            <th className="py-1.5 px-2 font-medium text-right">Last Seen</th>
            <th className="py-1.5 px-2 font-medium">Status</th>
            <th className="py-1.5 px-2 font-medium text-right">Score</th>
            <th className="py-1.5 px-2 font-medium text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => {
            const s = POI_STATUS[p.status] || POI_STATUS.Watch;
            const Icon = s.icon;
            return (
              <tr
                key={p.id}
                className="border-b border-border last:border-b-0 hover:bg-[hsl(var(--surface-elevated))]"
                data-testid={`ai-poi-row-${p.id}`}
              >
                <td className="py-1.5 px-2 font-mono font-bold">{p.id}</td>
                <td className="py-1.5 px-2">
                  <div className="truncate max-w-[140px]">{p.zone_name}</div>
                  <div className="micro-label text-[8.5px] text-muted-foreground">{p.camera_id}</div>
                </td>
                <td className="py-1.5 px-2">
                  <div className="flex flex-wrap gap-1">
                    {p.factors.map((f) => (
                      <span
                        key={f.type}
                        className="px-1 py-0.5 text-[9px] font-bold tracking-wide border border-border"
                        title={`${titleCase(f.type)} · ${f.count}`}
                      >
                        {titleCase(f.type)} · {f.count}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="py-1.5 px-2 text-right font-mono">{p.dwell_min}m</td>
                <td className="py-1.5 px-2 text-right font-mono text-muted-foreground">{relTime(p.last_seen)}</td>
                <td className="py-1.5 px-2">
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold tracking-wide border" style={{ color: s.color, borderColor: s.color }}>
                    <Icon className="w-3 h-3" /> {p.status}
                  </span>
                </td>
                <td className="py-1.5 px-2 text-right">
                  <span
                    className="inline-block px-1.5 py-0.5 font-mono text-[10px] font-bold"
                    style={{ background: s.color, color: "hsl(var(--background))" }}
                  >
                    {Math.round(p.score * 100)}
                  </span>
                </td>
                <td className="py-1.5 px-2 text-right">
                  <button
                    onClick={onOpenIncident}
                    className="text-[10px] text-[hsl(var(--primary))] hover:underline"
                    data-testid={`ai-poi-open-${p.id}`}
                  >
                    Review →
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const PredictiveCongestionTable = ({ rows = [] }) => {
  if (rows.length === 0) {
    return <div className="text-[11px] text-muted-foreground py-4 text-center">No congestion forecasts.</div>;
  }
  const horizons = rows[0]?.cells.map((c) => c.horizon_min) || [];
  return (
    <div className="overflow-x-auto" data-testid="ai-congestion-forecast">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-left font-mono text-[10px] text-muted-foreground border-b border-border">
            <th className="py-1.5 px-2 font-medium">Zone</th>
            <th className="py-1.5 px-2 font-medium text-right">Current</th>
            {horizons.slice(1).map((h) => (
              <th key={h} className="py-1.5 px-2 font-medium text-center">+{h}m</th>
            ))}
            <th className="py-1.5 px-2 font-medium text-right">Δ</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.zone_id}
              className="border-b border-border last:border-b-0 hover:bg-[hsl(var(--surface-elevated))]"
              data-testid={`ai-congestion-row-${r.zone_id}`}
            >
              <td className="py-1.5 px-2">{r.zone_name}</td>
              <td className="py-1.5 px-2 text-right font-mono">{Math.round(r.current * 100)}%</td>
              {r.cells.slice(1).map((c) => (
                <td key={c.horizon_min} className="py-1 px-1 text-center">
                  <div
                    className="inline-block min-w-[40px] py-1 px-2 font-mono text-[10px] font-bold"
                    style={{
                      background: congestionColor(c.pct),
                      color: "hsl(var(--background))",
                      opacity: 0.4 + c.pct * 0.6,
                    }}
                  >
                    {Math.round(c.pct * 100)}
                  </div>
                </td>
              ))}
              <td className={`py-1.5 px-2 text-right font-mono font-bold ${r.delta >= 0.1 ? "sev-critical" : r.delta > 0 ? "sev-medium" : "sev-low"}`}>
                {r.delta >= 0 ? "+" : ""}{Math.round(r.delta * 100)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const AIIntelligenceTab = ({ ai, onOpenIncident }) => {
  const d = ai || {
    kpis: {},
    anomaly_types: [],
    anomaly_matrix: [],
    pois: [],
    panic_curve: [],
    congestion_forecast: [],
  };
  const k = d.kpis || {};

  return (
    <div className="p-3 sm:p-4 space-y-3" data-testid="ai-tab">
      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <Pill icon={Activity}      label="High Anomalies"     value={k.high_anomalies ?? 0}                                   tone="danger"  testid="ai-kpi-high-anom" />
        <Pill icon={Activity}      label="Medium Anomalies"   value={k.medium_anomalies ?? 0}                                 tone="warn"    testid="ai-kpi-med-anom" />
        <Pill icon={OctagonAlert}  label="Alert POIs"         value={k.alert_pois ?? 0}                                       tone="danger"  testid="ai-kpi-alert-pois" />
        <Pill icon={UserSearch}    label="Investigate POIs"   value={k.investigate_pois ?? 0}                                 tone="warn"    testid="ai-kpi-inv-pois" />
        <Pill icon={Sparkles}      label="Panic Now"          value={`${Math.round((k.current_panic ?? 0) * 100)}`}            tone="primary" sub={k.panic_level} testid="ai-kpi-panic-now" />
        <Pill icon={TrendingUp}    label="Panic Peak (60m)"   value={`${Math.round((k.peak_panic ?? 0) * 100)}`}               tone="danger"  testid="ai-kpi-panic-peak" />
      </div>

      {/* Behavioral anomaly matrix */}
      <div className="panel p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="h-widget flex items-center gap-1">
            <Brain className="w-3 h-3" /> Behavioral Anomaly Matrix
          </div>
          <span className="micro-label">Zone × Anomaly Type · 0–100 Score</span>
        </div>
        <BehavioralAnomalyMatrix rows={d.anomaly_matrix} types={d.anomaly_types} />
      </div>

      {/* Panic forecast + Predictive congestion */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="panel p-3">
          <div className="h-widget mb-2 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Panic Prediction (Next 60m)
          </div>
          <PanicForecastChart curve={d.panic_curve} level={k.panic_level} />
        </div>
        <div className="panel p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="h-widget flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> Predictive Congestion
            </div>
            <span className="micro-label">Δ Vs Now</span>
          </div>
          <PredictiveCongestionTable rows={d.congestion_forecast} />
        </div>
      </div>

      {/* Suspicious activity scoring */}
      <div className="panel p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="h-widget flex items-center gap-1">
            <UserSearch className="w-3 h-3" /> Suspicious Activity Scoring
          </div>
          <span className="micro-label">Top {d.pois.length} Persons Of Interest</span>
        </div>
        <SuspiciousActivityTable rows={d.pois} onOpenIncident={onOpenIncident} />
      </div>
    </div>
  );
};

export default AIIntelligenceTab;
